import { useEffect, useMemo, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import {
  CustomerSummaryModal,
  type CustomerSummaryColumn,
  type CustomerSummarySectionAny,
} from '@/components/iem/shared/CustomerSummaryModal';
import type { ExchangeCartLine, ExchangeReturnDisposition } from './types';

export interface ExchangeCustomerSummaryModalProps {
  open: boolean;
  onClose: () => void;
  unitName?: string;
  memberName?: string;
  cartLines: ExchangeCartLine[];
}

const CONDITION_LABELS: Record<ExchangeReturnDisposition, string> = {
  recoverable: 'Serviceable',
  non_recoverable: 'Non-Serviceable',
};

type TurnInRow = {
  key: string;
  lin: string;
  nsn: string;
  name: string;
  receiptType: string;
  condition: string;
  quantity: number;
};

type ReplacementRow = {
  key: string;
  lin: string;
  nsn: string;
  name: string;
  quantity: number;
};

export const ExchangeCustomerSummaryModal = ({
  open,
  onClose,
  unitName,
  memberName,
  cartLines,
}: ExchangeCustomerSummaryModalProps) => {
  const [generatedAt, setGeneratedAt] = useState<Date>(() => new Date());

  useEffect(() => {
    if (open) {
      setGeneratedAt(new Date());
    }
  }, [open]);

  const turnInRows = useMemo<TurnInRow[]>(() => {
    const groups = new Map<string, TurnInRow>();
    cartLines.forEach((line) => {
      const key = [
        line.issuedItem.lin,
        line.issuedItem.nsn,
        line.issuedItem.name,
        line.issuedItem.receiptType ?? 'PHR',
        line.returnDisposition,
      ].join('::');
      const existing = groups.get(key);
      if (existing) {
        existing.quantity += 1;
        return;
      }
      groups.set(key, {
        key: `turnin:${key}`,
        lin: line.issuedItem.lin,
        nsn: line.issuedItem.nsn,
        name: line.issuedItem.name,
        receiptType: line.issuedItem.receiptType ?? 'PHR',
        condition: CONDITION_LABELS[line.returnDisposition] ?? '—',
        quantity: 1,
      });
    });
    return Array.from(groups.values());
  }, [cartLines]);

  const replacementRows = useMemo<ReplacementRow[]>(() => {
    const groups = new Map<string, ReplacementRow>();
    cartLines.forEach((line) => {
      const key = [
        line.replacementItem.lin,
        line.replacementItem.nsn,
        line.replacementItem.name,
      ].join('::');
      const existing = groups.get(key);
      if (existing) {
        existing.quantity += 1;
        return;
      }
      groups.set(key, {
        key: `replacement:${key}`,
        lin: line.replacementItem.lin,
        nsn: line.replacementItem.nsn,
        name: line.replacementItem.name,
        quantity: 1,
      });
    });
    return Array.from(groups.values());
  }, [cartLines]);

  const totals = useMemo(() => {
    const totalQty = cartLines.length;
    let serviceable = 0;
    let nonServiceable = 0;
    cartLines.forEach((line) => {
      if (line.returnDisposition === 'recoverable') {
        serviceable += 1;
      } else {
        nonServiceable += 1;
      }
    });
    return { totalQty, serviceable, nonServiceable };
  }, [cartLines]);

  const sections = useMemo(() => {
    const turnInColumns: CustomerSummaryColumn<TurnInRow>[] = [
      {
        header: 'LIN',
        width: '12%',
        render: (row) => <Typography variant="body2">{row.lin}</Typography>,
        printValue: (row) => row.lin,
      },
      {
        header: 'NSN',
        width: '15%',
        render: (row) => <Typography variant="body2">{row.nsn}</Typography>,
        printValue: (row) => row.nsn,
      },
      {
        header: 'Nomenclature',
        render: (row) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.name}
          </Typography>
        ),
        printValue: (row) => ({ primary: row.name }),
      },
      {
        header: 'Receipt Type',
        width: '12%',
        render: (row) => <Typography variant="body2">{row.receiptType}</Typography>,
        printValue: (row) => row.receiptType,
      },
      {
        header: 'Condition',
        width: '15%',
        render: (row) => <Typography variant="body2">{row.condition}</Typography>,
        printValue: (row) => row.condition,
      },
      {
        header: 'Qty',
        width: '8%',
        align: 'right',
        render: (row) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.quantity}
          </Typography>
        ),
        printValue: (row) => row.quantity,
      },
    ];

    const replacementColumns: CustomerSummaryColumn<ReplacementRow>[] = [
      {
        header: 'LIN',
        width: '12%',
        render: (row) => <Typography variant="body2">{row.lin}</Typography>,
        printValue: (row) => row.lin,
      },
      {
        header: 'NSN',
        width: '15%',
        render: (row) => <Typography variant="body2">{row.nsn}</Typography>,
        printValue: (row) => row.nsn,
      },
      {
        header: 'Nomenclature',
        render: (row) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.name}
          </Typography>
        ),
        printValue: (row) => ({ primary: row.name }),
      },
      {
        header: 'Qty',
        width: '8%',
        align: 'right',
        render: (row) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.quantity}
          </Typography>
        ),
        printValue: (row) => row.quantity,
      },
    ];

    const summary: CustomerSummarySectionAny[] = [
      {
        key: 'turn-in',
        title: 'Turn-In Items',
        rows: turnInRows,
        columns: turnInColumns,
        getRowKey: (row: TurnInRow) => row.key,
      },
      {
        key: 'replacements',
        title: 'Replacement Items',
        rows: replacementRows,
        columns: replacementColumns,
        getRowKey: (row: ReplacementRow) => row.key,
      },
    ];

    return summary;
  }, [turnInRows, replacementRows]);

  return (
    <CustomerSummaryModal
      open={open}
      onClose={onClose}
      title="Exchange Summary"
      workflowLabel="Exchange"
      unitName={unitName}
      memberName={memberName}
      sections={sections}
      generatedAt={generatedAt}
      summaryRight={
        <Stack spacing={0.25}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Total Qty: {totals.totalQty}
          </Typography>
          <Typography variant="body2">
            Serviceable: {totals.serviceable}
          </Typography>
          <Typography variant="body2">
            Non-Serviceable: {totals.nonServiceable}
          </Typography>
        </Stack>
      }
    />
  );
};
